import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useGetClientOverview } from '@/domain/client/useGetClientOverview';
import {
    useCompleteTask,
    useCreateManualActivity,
    useGetActivityLog,
    useGetPenOverview,
    useGetTasks,
    useIgnoreTask,
    useRefreshOperationalDashboard,
    useUnignoreTask,
} from '@/domain/operationalDashboard';
import { getDemoDisplayName } from '@/utils/demoMasking';

import OperationalDashboardView from './OperationalDashboardView';

const FALLBACK_PEN_SKELETON_COUNT = 4;

interface OperationalDashboardContainerProps {
    clientId: number;
    locationId: number;
}

export default function OperationalDashboardContainer(props: OperationalDashboardContainerProps) {
    const { clientId, locationId } = props;
    const routerLocation = useLocation();
    const { t } = useTranslation();
    const { isDemoMode } = useDemoMode();

    const { data: clientOverview } = useGetClientOverview(clientId);
    const internalPage = routerLocation.pathname.startsWith('/internal');

    const tasksQuery = useGetTasks(locationId);
    const {
        data: taskList,
        isLoading: tasksLoading,
        error: tasksError,
        isFetched: tasksFetched,
    } = tasksQuery;

    const activityQuery = useGetActivityLog(locationId, undefined, { enabled: tasksFetched });
    const { data: activityLog, isLoading: activityLoading, error: activityError } = activityQuery;

    const penQuery = useGetPenOverview(locationId);
    const { data: penOverview, isLoading: penLoading, error: penError } = penQuery;

    const { mutateAsync: completeTask } = useCompleteTask();
    const { mutateAsync: ignoreTask } = useIgnoreTask();
    const { mutateAsync: unignoreTask } = useUnignoreTask();
    const { mutateAsync: createManualActivity } = useCreateManualActivity();

    const invalidateQueries = useRefreshOperationalDashboard();

    // The view fires these and moves on, so a failure is told here rather than rejected.
    const runAction = useCallback(
        async (action: () => Promise<void>, failedMessage: string) => {
            try {
                await action();
                invalidateQueries();
            } catch (error: unknown) {
                ToastError(
                    apiErrorContent(error, failedMessage),
                    'operational-dashboard-action-error'
                );
            }
        },
        [invalidateQueries]
    );

    const handleCompleteTask = useCallback(
        (taskId: number) =>
            runAction(() => completeTask({ taskId }), t('operationalDashboard.task.actionFailed')),
        [completeTask, runAction, t]
    );

    const handleIgnoreTask = useCallback(
        (params: { taskId: number; ignoredUntil?: Date; ignoredPermanently?: boolean }) =>
            runAction(() => ignoreTask(params), t('operationalDashboard.task.actionFailed')),
        [ignoreTask, runAction, t]
    );

    const handleUnignoreTask = useCallback(
        (taskId: number) =>
            runAction(() => unignoreTask({ taskId }), t('operationalDashboard.task.actionFailed')),
        [unignoreTask, runAction, t]
    );

    const handleAddActivity = useCallback(
        (params: {
            locationId: number;
            title: string;
            cageId?: number;
            performedAt?: string;
            comment?: string;
        }) =>
            runAction(
                () => createManualActivity(params),
                t('operationalDashboard.addActivityModal.addFailed')
            ),
        [createManualActivity, runAction, t]
    );

    const overviewLocation = clientOverview?.locations.find(
        (location) => location.locationId === locationId
    );
    const expectedPenCount = overviewLocation?.activeCages.length ?? FALLBACK_PEN_SKELETON_COUNT;
    const locationName = useMemo(() => {
        if (isDemoMode && !internalPage) {
            return getDemoDisplayName(locationId, 'location', t);
        }

        return penOverview?.locationName ?? overviewLocation?.locationName ?? '';
    }, [
        internalPage,
        isDemoMode,
        locationId,
        overviewLocation?.locationName,
        penOverview?.locationName,
        t,
    ]);

    return (
        <OperationalDashboardView
            locationName={locationName}
            locationId={locationId}
            taskList={taskList}
            activityLog={activityLog}
            penOverview={penOverview}
            tasksLoading={tasksLoading}
            activityLoading={activityLoading}
            penLoading={penLoading}
            expectedPenCount={expectedPenCount}
            error={tasksError ?? activityError ?? penError}
            retry={retryFailedQueries(tasksQuery, activityQuery, penQuery)}
            onCompleteTask={handleCompleteTask}
            onIgnoreTask={handleIgnoreTask}
            onUnignoreTask={handleUnignoreTask}
            onAddActivity={handleAddActivity}
        />
    );
}
